<script setup>
/**
 *  Usage:
 *
 *  <ui-tabbed>
 *      <ui-tabbed-tab
 *          title="Tab One"
 *          icon="fa-regular fa-circle text-myflowGreen"
 *          value="tab-one"
 *          :active="true"
 *      >
 *          <h2>Tab one</h2>
 *          <p>Lorem ipsum</p>
 *      </ui-tabbed-tab>
 *
 *      <ui-tabbed-tab
 *          title="Tab Two"
 *          icon="fa-regular fa-circle text-myflowRed"
 *          value="tab-two"
 *          :active="false"
 *      >
 *          <h2>Tab two</h2>
 *          <p>Lorem ipsum again</p>
 *      </ui-tabbed-tab>
 *  </ui-tabbed>
 */


import { ref, provide, watchEffect, useSlots, onMounted, watch, computed, nextTick } from 'vue'
import TabList from './_tabList.vue'

const props = defineProps({
    nametab: {
        type: String
    },
    classname: {
        type: String,
        default: null
    },
    trackState: {
        type: Boolean,
        required: false,
        default: true,
    },
    preselectedTab: {
        type: String,
        required: false,
        default: null,
    },
    responsive: {
        type: Boolean,
        required: false,
        default: true,
    },
    slotContainerClasses: {
        type: String,
        required: false,
        default: 'pt-4'
    },
    scrollOverflow: {
        type: Boolean,
        required: false,
        default: false,
    },
    displayInTabBar: {
        type: Boolean,
        required: false,
        default: false,
    }
})

const emit = defineEmits(['changestatustab'])
const slots = useSlots()

const tabBarTarget = '#top-bar-tab-bar';

const formIsFilled = ref(false)
const tabTitles = computed(() => {
    return (slots.default?.() ?? [])
        .map((tab) => tab.props)
        .filter(Boolean)
})
const activeTab = computed(() => {
    return tabTitles.value.find((tab) => tab.active === true) ?? tabTitles.value[0] ?? null
})
const selectedTitle = ref(activeTab.value?.value ?? null)


const tabBarFound = ref(null)

provide('selectedTitle', selectedTitle)

const tabListClasses = computed(() => [
    props.classname,
    props.responsive ? 'overflow-x-auto @lg:overflow-visible' : 'overflow-x-auto',
    props.scrollOverflow ? '@lg:overflow-x-auto' : '',
])

watchEffect(() => {
    if (selectedTitle.value && formIsFilled.value) {
        emit('changestatustab', selectedTitle.value)
    }
    formIsFilled.value = true
})

const selectTab = (tab) => {
    selectedTitle.value = tab
    emit('changestatustab', selectedTitle.value)
}

watch(selectedTitle, (newValue) => {
    if (! props.trackState) {
        return
    }
    if (newValue) {
        window.location.hash = newValue
    }
})

defineExpose({
    selectTab
})

onMounted(async () => {
    if (props.displayInTabBar) {
        await nextTick()
        tabBarFound.value = document.querySelector(tabBarTarget) !== null
    }
})

onMounted(() => {
    const presetTab = window.location.hash.replace('#', '')

    if (presetTab && tabTitles.value.find(tab => tab.value === presetTab)) {
        selectedTitle.value = presetTab
    } else if (props.preselectedTab) {
        selectedTitle.value = props.preselectedTab
    } else if (! selectedTitle.value && activeTab.value) {
        selectedTitle.value = activeTab.value.value
    }
})

</script>
<template>
    <div class="@container w-full max-w-screen">
        <teleport
            v-if="displayInTabBar && tabBarFound === true"
            :to="tabBarTarget"
        >
            <div class="@container w-full">
                <tab-list
                    v-bind:id="nametab"
                    v-bind:class="tabListClasses"
                    :tabs="tabTitles"
                    :selected="selectedTitle"
                    @select="selectTab"
                />
            </div>
        </teleport>
        <tab-list
            v-else-if="! displayInTabBar || tabBarFound === false"
            v-bind:id="nametab"
            v-bind:class="tabListClasses"
            :tabs="tabTitles"
            :selected="selectedTitle"
            @select="selectTab"
        />
        <div :class="slotContainerClasses">
            <slot/>
        </div>
    </div>
</template>
